import React,{ useEffect,useState } from 'react';
import AutomationCard from './AutomationCard';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../context/AuthContext';
import { Lightbulb, RotateCcw,Plus } from 'lucide-react';
import PageHeader from '../ui/page-header';
import AutomationStartDialog from './AutomationStartDialog';
import LinkSwitchesDialog from './LinkSwitchesDialog';

const AutomationList = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEditing, setLinkEditing] = useState(null);
  const { apiRequest } = useAuth();
  const API_BASE_URL = '';
  const API_PATH = '/api';

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      setAutomations(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching automations:', err);
      setError('Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAutomations();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchAutomations();
  }, []);

  const handleCreateAutomation = async () => {
    try {
      const defaultAutomation = {
        name: 'New Automation',
        description: '',
        active: true,
        conditions: [],
        actions: []
      };

      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defaultAutomation),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const newAutomation = data;
      setAutomations(prevAutomations => [...prevAutomations, newAutomation]);
    } catch (err) {
      console.error('Error creating automation:', err);
      setError('Failed to create automation');
    }
  };

  const handleDeleteAutomation = async (id) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      setAutomations(prevAutomations => prevAutomations.filter(a => a.id !== id));
    } catch (err) {
      console.error('Error deleting automation:', err);
      setError('Failed to delete automation');
    }
  };

  const handleToggleAutomation = async (id) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${id}/toggle`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const updatedAutomation = data;
      setAutomations(prevAutomations =>
        prevAutomations.map(a => a.id === id ? updatedAutomation : a)
      );
    } catch (err) {
      console.error('Error toggling automation:', err);
      setError('Failed to toggle automation');
    }
  };

  const handlePickStart = (kind) => {
    setChooserOpen(false);
    if (kind === 'link') {
      setLinkEditing(null);
      setLinkOpen(true);
    } else {
      handleCreateAutomation();
    }
  };

  const openLinkEdit = (automation) => {
    setLinkEditing(automation);
    setLinkOpen(true);
  };

  const handleLinkSaved = (saved, isEdit) => {
    setAutomations((prev) =>
      isEdit ? prev.map((a) => (a.id === saved.id ? saved : a)) : [...prev, saved]
    );
  };

  if (loading) {
    return (
      <div className="px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading automations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Automations"
        subtitle="Automate your home"
        onAdd={() => setChooserOpen(true)}
        menuItems={[
          { label: 'Refresh', icon: RotateCcw, onClick: handleRefresh, disabled: refreshing }
        ]}
      />

      <div className="px-4 py-6 space-y-6">

      {automations.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Lightbulb className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-base font-medium mb-2">No automations yet</h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
              Create your first automation to make your home work for you automatically
            </p>
            <Button onClick={() => setChooserOpen(true)} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Create Automation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {automations.map(automation => (
            <AutomationCard
              key={automation.id}
              automation={automation}
              onDelete={handleDeleteAutomation}
              onToggle={handleToggleAutomation}
              onEditLink={openLinkEdit}
            />
          ))}
        </div>
      )}
      </div>

      <AutomationStartDialog open={chooserOpen} onOpenChange={setChooserOpen} onPick={handlePickStart} />
      <LinkSwitchesDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        automation={linkEditing}
        onSaved={handleLinkSaved}
      />
    </div>
  );
};

export default AutomationList;